import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import RelatedServicesNav from '../components/RelatedServicesNav';
import StickyMobileCta from '../components/StickyMobileCta';
import {
  FlaskConical, Microscope, ScanLine, HeartPulse, Activity, Zap, Droplet, Eye,
  Bug, ClipboardCheck, ClipboardList, MessageSquare, Clock, ShieldCheck, HandHeart,
  BookOpen, AlertCircle, CheckCircle2, MapPin, Phone, MessageCircle, CalendarDays,
  ArrowRight, Sparkles, Stethoscope,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { CONTACT, GOOGLE_RATING } from '../data/mock';
import { cdnImg } from '../utils/cdn';
import { trackCallClick, trackWhatsAppClick, trackBookButtonClick } from '../lib/analytics';

const ASSET = 'https://customer-assets.emergentagent.com/job_petcare-preview-1/artifacts';
const DR_PORTRAIT = `${ASSET}/jnhxjj9q_WhatsApp%20Image%202026-07-08%20at%2012.31.40.jpeg`;
const HERO_IMAGE = 'https://images.unsplash.com/photo-1738778151587-032287ae9f90?auto=format&fit=crop&w=1400&q=80';

const TITLE = 'Pet Diagnostics in Noida | In-House Lab, ECG & Imaging | Ichi & Ori Pet Care';
const DESCRIPTION = 'Comprehensive pet diagnostics in Noida. Ichi & Ori Pet Care offers in-house CBC, ECG, blood pressure, microscopy, digital DR X-ray and accredited partner-lab testing for dogs and cats.';
const CANONICAL = 'https://ichiandoripetcare.com/pet-diagnostics-noida';
const set = (sel, attr, val) => { const el = document.head.querySelector(sel); if (el) el.setAttribute(attr, val); };

const WHY_DIAGNOSTICS = [
  { Icon: Eye,           title: 'See what an exam cannot',    text: 'Blood, urine, imaging and cardiac readings uncover changes long before they show up as symptoms at home.' },
  { Icon: Clock,         title: 'Faster, cleaner decisions',  text: 'In-house diagnostics mean many results are ready in the same visit, which shortens the path from concern to treatment.' },
  { Icon: ShieldCheck,   title: 'Safer treatment plans',      text: 'Diagnostics guide safe drug choice, anaesthesia planning and dosing, especially in seniors and pets with chronic conditions.' },
  { Icon: Sparkles,      title: 'Early disease detection',    text: 'Kidney, liver, thyroid and heart issues often begin quietly. Screening catches them in the window where treatment works best.' },
  { Icon: ClipboardCheck,title: 'Objective progress tracking',text: 'Repeat testing shows whether a treatment is working, so plans can be adjusted based on evidence, not guesswork.' },
  { Icon: MessageSquare, title: 'Clear, honest explanations', text: 'Every result is walked through in plain language, with the veterinarian, so you leave understanding what it means.' },
];

const TESTS_INHOUSE = [
  { Icon: FlaskConical, title: 'Complete Blood Count (CBC)',   text: 'Screens for infection, anaemia, inflammation and immune-cell changes across dogs and cats of all ages.' },
  { Icon: HeartPulse,   title: 'ECG (Electrocardiogram)',      text: 'Records the electrical activity of the heart to evaluate rhythm, rate and pre-anaesthetic cardiac safety.' },
  { Icon: Activity,     title: 'Blood pressure monitoring',    text: 'Especially important in older cats, dogs with kidney or heart disease and any pet on long-term medication.' },
  { Icon: Microscope,   title: 'Microscopy',                   text: 'Skin scrapings, ear cytology, faecal exams and urine sediment reviewed directly in the clinic, often within minutes.' },
  { Icon: Droplet,      title: 'Urine analysis',               text: 'Colour, concentration, protein, glucose and sediment review, catching urinary and kidney conditions early.' },
  { Icon: ScanLine,     title: 'Digital DR X-Ray',             text: 'In-house digital radiography for orthopaedic, chest and abdominal concerns, reviewed on-screen within minutes.' },
];

const TESTS_PARTNER = [
  { Icon: FlaskConical, title: 'Full biochemistry panels',     text: 'Comprehensive liver, kidney, glucose, electrolyte and protein evaluation through accredited partner laboratories.' },
  { Icon: Microscope,   title: 'Histopathology & cytology',    text: 'Tissue and cell samples are sent to trusted labs for expert pathologist review, guiding definitive diagnosis.' },
  { Icon: Bug,          title: 'Infectious disease screening', text: 'Tick-borne diseases, feline retroviruses, canine parvovirus, distemper and selected fungal or bacterial panels.' },
  { Icon: Zap,          title: 'Hormonal & thyroid testing',   text: 'Structured evaluation of thyroid, adrenal and reproductive hormone imbalances, common in middle-aged and senior pets.' },
];

const PROCESS = [
  { step: '01', title: 'Consultation & clinical assessment', text: 'The veterinarian examines your pet, reviews history and decides which diagnostics will meaningfully change the plan.' },
  { step: '02', title: 'Sample collection',                  text: 'Blood, urine, faecal or skin samples are collected gently, with minimal restraint and calm handling for anxious pets.' },
  { step: '03', title: 'In-house or partner testing',        text: 'Time-critical tests run in-clinic. Complex or specialised tests are sent to accredited partner laboratories with clear turnaround times.' },
  { step: '04', title: 'Results reviewed together',          text: 'Findings are explained in plain language, side by side with what they mean for your pet’s current condition and next steps.' },
  { step: '05', title: 'Adjusted treatment plan',            text: 'The plan is either confirmed, refined or safely paused based on the evidence. Nothing changes without a proper conversation.' },
  { step: '06', title: 'Follow-up testing',                  text: 'For chronic and monitored conditions, repeat diagnostics are planned in advance so care remains proactive, not reactive.' },
];

const WHEN_TEST = [
  'Sudden weight loss or gain',
  'Loss of appetite or increased thirst',
  'Repeated vomiting or diarrhea',
  'Persistent scratching or skin issues',
  'Limping, stiffness or reluctance to move',
  'Behavioural changes or lethargy',
  'Pre-anaesthetic and pre-surgical checks',
  'Ongoing management of senior pets',
];

const WHY_ICHI = [
  'In-house CBC, ECG, BP and microscopy',
  'Accredited partner labs',
  'Same-visit clinical decisions',
  'Digital record keeping',
  'Plain-language result explanations',
  'Pre-anaesthetic screening built in',
  'Structured follow-up testing',
  'Honest guidance on necessary vs optional',
];

const LOCATIONS = ['Sector 90','Sector 137','Sector 143','Sector 168','Sector 93','Sector 100','Noida Expressway','Nearby residential societies'];

const FAQS = [
  { q: 'What diagnostics are available in-house?',       a: 'We offer CBC, ECG, blood pressure monitoring, microscopy (skin scraping, ear cytology, faecal and urine analysis) and Digital DR X-ray in-clinic. Most results are available within the same visit, so decisions do not wait for outside labs.' },
  { q: 'What tests go to partner laboratories?',         a: 'Full biochemistry panels, histopathology, cytology, infectious disease screening and hormonal or thyroid testing are performed through accredited partner laboratories. Turnaround times are shared upfront when samples are collected.' },
  { q: 'How long does it take to get results?',          a: 'In-house tests are usually reviewed the same day, often during your visit. Partner-lab results generally return within 24 to 72 hours depending on the test. We share expected turnaround at the time of sample collection.' },
  { q: 'Are diagnostics safe for pets?',                 a: 'The tests we perform are non-invasive or minimally invasive. Blood collection uses fine needles, microscopy uses small samples and X-rays use low-dose Digital DR equipment. Diagnostics are only recommended when they will meaningfully change the plan.' },
  { q: 'Is fasting required before blood tests?',        a: 'Basic CBC and infectious screening tests usually do not require fasting. Full biochemistry panels are more accurate on a fasted sample. Specific instructions are shared at booking.' },
  { q: 'Are diagnostics important for senior pets?',     a: 'Yes. In pets over eight years of age, twice-yearly diagnostics catch changes in kidney, liver, thyroid and heart function while they are still very manageable, which can significantly improve quality and length of life.' },
  { q: 'Do you perform pre-anaesthetic workup?',         a: 'Yes. Before any planned surgery or dental procedure, we recommend pre-anaesthetic blood work, ECG and physical examination. This lets us design a safer anaesthesia plan for each individual pet.' },
  { q: 'Can I get a copy of the report?',                a: 'Absolutely. All reports and images are stored digitally and can be shared with you by WhatsApp or email, and forwarded to any referral clinic your pet may visit later.' },
  { q: 'How much do diagnostics cost?',                  a: 'Costs vary by test. Basic in-house tests are affordable and quick. Comprehensive panels or specialised tests cost more. A clear estimate is always shared before samples are collected, with no hidden fees.' },
  { q: 'How do I book a diagnostic appointment?',        a: 'Use the Request Appointment button on this page, message us on WhatsApp at +91 9560925999, or call the clinic between 10:30 AM and 8:30 PM. A short note on symptoms helps us plan the right set of tests.' },
];

const PetDiagnosticsNoida = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    const orig = {
      d: document.head.querySelector('meta[name="description"]')?.getAttribute('content'),
      c: document.head.querySelector('link[rel="canonical"]')?.getAttribute('href'),
    };
    set('meta[name="description"]', 'content', DESCRIPTION);
    set('link[rel="canonical"]', 'href', CANONICAL);
    set('meta[property="og:title"]', 'content', TITLE);
    set('meta[property="og:description"]', 'content', DESCRIPTION);
    set('meta[property="og:url"]', 'content', CANONICAL);
    return () => {
      if (orig.d != null) set('meta[name="description"]', 'content', orig.d);
      if (orig.c != null) set('link[rel="canonical"]', 'href', orig.c);
    };
  }, []);

  const wa = `https://wa.me/${CONTACT.phoneRaw.replace(/\D/g, '')}?text=${encodeURIComponent("Hi, I'd like to book pet diagnostics at Ichi & Ori Pet Care.")}`;
  const faqSchema = { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: FAQS.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) };
  const vetSchema = {
    '@context': 'https://schema.org', '@type': 'VeterinaryCare',
    name: 'Ichi and Ori Pet Care, Pet Diagnostics Clinic in Noida',
    url: CANONICAL, telephone: CONTACT.phoneRaw, priceRange: '₹₹',
    address: { '@type': 'PostalAddress', streetAddress: 'G11, Tower C, Noida World One', addressLocality: 'Sector 90, Noida', addressRegion: 'Uttar Pradesh', postalCode: '201305', addressCountry: 'IN' },
    aggregateRating: { '@type': 'AggregateRating', ratingValue: String(GOOGLE_RATING?.rating || 4.9), reviewCount: String(GOOGLE_RATING?.count || 107), bestRating: '5' },
    areaServed: { '@type': 'City', name: 'Noida' },
  };
  const breadcrumbSchema = {
    '@context': 'https://schema.org', '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://ichiandoripetcare.com/' },
      { '@type': 'ListItem', position: 2, name: 'Pet Diagnostics in Noida', item: CANONICAL },
    ],
  };

  return (
    <div className="bg-white" data-testid="pet-diagnostics-noida-page">
      <Helmet>
        <title>{TITLE}</title>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(vetSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* 1 — HERO */}
      <section className="relative bg-gradient-to-br from-[#1a0b3d] via-[#3b1d76] to-[#5b3eb5] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-25 [background-image:radial-gradient(circle_at_25%_20%,#e870b6_0%,transparent_40%),radial-gradient(circle_at_80%_75%,#5b3eb5_0%,transparent_45%)]" />
        <div className="relative max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10 py-16 sm:py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-16 items-center">
          <div>
            <span className="inline-flex items-center gap-2 px-4 py-2 sm:py-1.5 rounded-full bg-[#e870b6]/20 border border-[#e870b6]/40 text-[#ffc1e0] text-[11px] sm:text-xs font-bold tracking-[0.18em] mb-6">
              <FlaskConical className="w-3.5 h-3.5 flex-shrink-0" />PET DIAGNOSTICS · NOIDA
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.03] mb-6">Modern Pet Diagnostics in Noida.</h1>
            <p className="text-base sm:text-lg text-white/85 leading-relaxed mb-9 max-w-xl">In-house CBC, ECG, blood pressure, microscopy and <Link to="/pet-x-ray-noida" className="underline decoration-white/40 underline-offset-4 hover:decoration-white text-white">digital veterinary X-ray</Link>, supported by accredited partner laboratories for full biochemistry and specialised testing.</p>
            <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-3 sm:gap-4">
              <Link to="/book" onClick={() => trackBookButtonClick('pet_diagnostics_hero')} className="w-full sm:w-auto">
                <Button data-testid="pet-diagnostics-hero-book-btn" className="w-full sm:w-auto bg-[#e870b6] hover:bg-[#d85ba0] text-white rounded-full px-7 py-6 text-base font-semibold shadow-xl"><CalendarDays className="w-5 h-5 mr-2" />Request Appointment</Button>
              </Link>
              <a href={wa} target="_blank" rel="noreferrer" onClick={() => trackWhatsAppClick('pet_diagnostics_hero')} className="w-full sm:w-auto">
                <Button data-testid="pet-diagnostics-hero-wa-btn" className="w-full sm:w-auto bg-[#25d366] hover:bg-[#1eb858] text-white rounded-full px-7 py-6 text-base font-semibold shadow-xl"><MessageCircle className="w-5 h-5 mr-2" />Chat on WhatsApp</Button>
              </a>
              <a href={`tel:${CONTACT.phoneRaw}`} onClick={() => trackCallClick('pet_diagnostics_hero')} className="w-full sm:w-auto sm:ml-1">
                <span className="inline-flex items-center gap-2 text-white/85 hover:text-white font-medium text-[14px] sm:text-[15px] underline decoration-white/30 underline-offset-4 hover:decoration-white transition-colors px-1 py-2">
                  <Phone className="w-4 h-4" /> Call Now
                </span>
              </a>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl border border-white/15 aspect-[4/5]">
              <img src={HERO_IMAGE} alt="Laboratory microscope with petri dish and glass flask on a clean white bench, representing pet diagnostics at Ichi and Ori Pet Care, Noida — placeholder image" loading="eager" fetchPriority="high" decoding="async" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <RelatedServicesNav current="/pet-diagnostics-noida" />

      {/* 2 — WHY DIAGNOSTICS MATTER */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">WHY IT MATTERS</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">Answers that guide the right next step.</h2>
            <p className="text-[#3b1d76]/80 text-base sm:text-lg leading-relaxed mt-5">Great veterinary care is a conversation between what you see at home, what the veterinarian examines and what diagnostics reveal underneath. Each piece changes what the next visit looks like.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHY_DIAGNOSTICS.map((r, i) => (
              <div key={i} className="bg-[#fdf7fb] rounded-2xl p-7 border border-[#f3d6f5]/60 hover:shadow-md transition-all flex flex-col h-full">
                <div className="w-12 h-12 rounded-2xl bg-white border border-[#f5d3e6] flex items-center justify-center mb-4 flex-shrink-0"><r.Icon className="w-5 h-5 text-[#e870b6]" /></div>
                <h3 className="font-bold text-[#1a0b3d] text-lg mb-2 leading-snug">{r.title}</h3>
                <p className="text-sm text-[#3b1d76]/85 leading-relaxed">{r.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3 — IN-HOUSE DIAGNOSTICS */}
      <section className="bg-[#1a0b3d] text-white py-20 sm:py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30 [background-image:radial-gradient(circle_at_15%_20%,#5b3eb5_0%,transparent_45%),radial-gradient(circle_at_85%_80%,#e870b6_0%,transparent_45%)]" />
        <div className="relative max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#ffc1e0] mb-4">IN-HOUSE DIAGNOSTICS</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[1.08]">Same-visit answers, no waiting around.</h2>
            <p className="text-white/80 text-base sm:text-lg leading-relaxed mt-5">Tests we can run in-clinic, so your pet&rsquo;s treatment plan is decided while you are still with the veterinarian.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {TESTS_INHOUSE.map((s, i) => (
              <div key={i} className="bg-white/5 backdrop-blur rounded-2xl p-6 border border-white/10 flex flex-col h-full">
                <div className="w-11 h-11 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center mb-4 flex-shrink-0"><s.Icon className="w-5 h-5 text-[#ffc1e0]" /></div>
                <h3 className="font-bold text-white text-base mb-2 leading-snug">{s.title}</h3>
                <p className="text-sm text-white/75 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4 — PARTNER LAB TESTS */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">PARTNER LABORATORY TESTING</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">Specialised tests, accredited labs.</h2>
            <p className="text-[#3b1d76]/80 text-base sm:text-lg leading-relaxed mt-5">For complex, comprehensive or highly specialised testing, we work with trusted, accredited laboratories to give you the same quality your family doctor would trust for you.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {TESTS_PARTNER.map((s, i) => (
              <div key={i} className="bg-[#fdf7fb] rounded-2xl p-6 border border-[#f3d6f5]/60 flex flex-col h-full">
                <div className="w-11 h-11 rounded-xl bg-white border border-[#f5d3e6] flex items-center justify-center mb-4 flex-shrink-0"><s.Icon className="w-5 h-5 text-[#e870b6]" /></div>
                <h3 className="font-bold text-[#1a0b3d] text-base mb-2 leading-snug">{s.title}</h3>
                <p className="text-sm text-[#3b1d76]/80 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5 — HOW IT WORKS */}
      <section className="bg-[#fdf7fb] py-20 sm:py-24">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">HOW IT WORKS</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">From sample to explained result.</h2>
          </div>
          <ol className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {PROCESS.map((s, i) => (
              <li key={i} className="bg-white rounded-2xl p-6 border border-[#f3d6f5]/60 flex gap-5 items-start">
                <span className="text-4xl font-extrabold text-[#e870b6]/50 leading-none tracking-tight">{s.step}</span>
                <div>
                  <h3 className="font-bold text-[#1a0b3d] text-lg mb-1.5 leading-snug">{s.title}</h3>
                  <p className="text-sm text-[#3b1d76]/85 leading-relaxed">{s.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* 6 — WHEN TO TEST */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">WHEN TO TEST</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">When diagnostics are usually the right call.</h2>
            <p className="text-[#3b1d76]/80 text-base sm:text-lg leading-relaxed mt-5">Not every visit needs testing. The signs and situations below are the ones where diagnostics most often change what we do next.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {WHEN_TEST.map((s, i) => (
              <div key={i} className="flex items-start gap-3 bg-[#fdf7fb] rounded-2xl px-5 py-4 border border-[#f3d6f5]/60">
                <span className="w-7 h-7 rounded-full bg-white border border-[#f5d3e6] flex items-center justify-center flex-shrink-0 mt-0.5"><ArrowRight className="w-3.5 h-3.5 text-[#e870b6]" /></span>
                <span className="text-[#1a0b3d] text-[15px] font-semibold leading-snug">{s}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7 — WHY CHOOSE ICHI & ORI */}
      <section className="bg-[#fdf7fb] py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10 grid grid-cols-1 lg:grid-cols-[minmax(240px,360px)_1fr] gap-10 lg:gap-14 items-start">
          <div>
            <div className="rounded-[24px] overflow-hidden shadow-xl border border-[#f5d3e6] bg-white">
              <img src={cdnImg(DR_PORTRAIT, { w: 720, h: 900, fit: 'cover', a: 'center' })} alt="Dr Kaushal Singh Yadav, veterinarian at Ichi and Ori Pet Care, Sector 90 Noida" loading="lazy" decoding="async" className="w-full h-auto object-cover" />
            </div>
          </div>
          <div>
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">WHY CHOOSE ICHI &amp; ORI</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08] mb-6">Diagnostics that change the plan.</h2>
            <div className="space-y-4 text-[#3b1d76]/85 text-[15px] sm:text-[16px] leading-[1.85] mb-8">
              <p>Diagnostics are only ordered when they will meaningfully change what we recommend. You always know why a test was chosen, what it will cost and how it will help.</p>
              <p>Every result is explained in plain language, alongside the physical exam and your observations at home. Nothing is left as jargon on paper.</p>
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
              {WHY_ICHI.map((t, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-7 h-7 rounded-full bg-white border border-[#f5d3e6] flex items-center justify-center flex-shrink-0 mt-0.5"><CheckCircle2 className="w-4 h-4 text-[#e870b6]" /></span>
                  <span className="text-[#1a0b3d] text-[15px] font-semibold">{t}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <Link to="/book" onClick={() => trackBookButtonClick('pet_diagnostics_why')} className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#1a0b3d] hover:bg-[#3b1d76] text-white text-sm font-semibold transition-colors">Book Diagnostic Consultation <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* 8 — AREAS SERVED */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">AREAS WE SERVE</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">Trusted pet diagnostics across Noida.</h2>
          </div>
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
            {LOCATIONS.map((l, i) => (
              <span key={i} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#fdf7fb] border border-[#f3d6f5] text-[#1a0b3d] text-sm font-semibold">
                <MapPin className="w-3.5 h-3.5 text-[#e870b6]" /> {l}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* 9 — FAQ */}
      <section className="bg-[#fdf7fb] py-20 sm:py-24">
        <div className="max-w-[880px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center mb-12">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">FREQUENTLY ASKED</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">Pet diagnostics questions, answered clearly.</h2>
          </div>
          <Accordion type="single" collapsible defaultValue="faq-0" className="divide-y divide-[#f5d3e6] border-y border-[#f5d3e6] bg-white rounded-2xl" data-testid="pet-diagnostics-faq">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="px-4">
                <AccordionTrigger data-testid={`faq-q-${i}`} className="text-left text-[#1a0b3d] font-semibold text-base sm:text-lg hover:no-underline py-5 sm:py-6">{f.q}</AccordionTrigger>
                <AccordionContent className="text-[#3b1d76]/85 text-sm sm:text-base leading-relaxed pb-6">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* 10 — FINAL CTA */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="rounded-[32px] bg-gradient-to-br from-[#5b3eb5] to-[#1a0b3d] text-white py-16 sm:py-20 px-6 relative overflow-hidden">
            <div className="absolute inset-0 opacity-25 [background-image:radial-gradient(circle_at_20%_30%,#e870b6_0%,transparent_45%)]" />
            <div className="relative text-center">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-4 leading-[1.08]">The right test, at the right time, changes everything.</h2>
              <p className="text-white/85 mb-10 max-w-xl mx-auto text-base sm:text-lg leading-relaxed">Book a consultation and let the veterinarian help you decide what diagnostics your pet actually needs, and what can safely wait.</p>
              <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-4">
                <Link to="/book" onClick={() => trackBookButtonClick('pet_diagnostics_final')} className="w-full sm:w-auto">
                  <Button data-testid="pet-diagnostics-final-book-btn" className="w-full sm:w-auto bg-[#e870b6] hover:bg-[#d85ba0] rounded-full px-8 py-6 font-semibold shadow-xl"><CalendarDays className="w-5 h-5 mr-2" />Request Appointment</Button>
                </Link>
                <a href={wa} target="_blank" rel="noreferrer" onClick={() => trackWhatsAppClick('pet_diagnostics_final')} className="w-full sm:w-auto">
                  <Button data-testid="pet-diagnostics-final-wa-btn" className="w-full sm:w-auto bg-[#25d366] hover:bg-[#1eb858] rounded-full px-8 py-6 font-semibold shadow-xl"><MessageCircle className="w-5 h-5 mr-2" />WhatsApp</Button>
                </a>
                <a href={`tel:${CONTACT.phoneRaw}`} onClick={() => trackCallClick('pet_diagnostics_final')} className="w-full sm:w-auto sm:ml-1">
                  <span className="inline-flex items-center gap-2 text-white/85 hover:text-white font-medium text-[14px] sm:text-[15px] underline decoration-white/30 underline-offset-4 hover:decoration-white transition-colors px-1 py-2">
                    <Phone className="w-4 h-4" /> Call Now
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
      <StickyMobileCta source="petdiagnostics" />
    </div>
  );
};

export default PetDiagnosticsNoida;
